/**
 * Words management hook.
 * Handles fetching, creating, updating, and deleting words with pagination.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocalStorage } from './useLocalStorage';
import apiClient from '../services/api';
import { groupWordsByDate } from '../utils/dateFormatter';
import config from '../config/config';

export const useWords = (initialGroupBy = 'day') => {
  const [words, setWords] = useState([]);
  const [groupedWords, setGroupedWords] = useState({});
  const [groupBy, setGroupBy] = useLocalStorage('wordList.groupBy', initialGroupBy);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const searchDebounceRef = useRef(null);

  /**
   * Fetch words from API
   */
  const fetchWords = useCallback(async (append = false, search = '') => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      setError(null);

      const params = {
        limit: config.defaultPageSize,
      };

      // Add search query if provided
      if (search) {
        params.search = search;
      }

      // Add pagination key if appending
      if (append && lastEvaluatedKey) {
        params.lastEvaluatedKey = JSON.stringify(lastEvaluatedKey);
      }

      const response = await apiClient.getWords(params);
      const newWords = response.items || [];

      if (append) {
        setWords((prev) => [...prev, ...newWords]);
      } else {
        setWords(newWords);
      }

      // Update pagination state
      setLastEvaluatedKey(response.lastEvaluatedKey || null);
      setHasMore(!!response.lastEvaluatedKey);
    } catch (err) {
      console.error('Error fetching words:', err);
      setError(err.response?.data?.error || 'Failed to fetch words');
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  }, [isLoading, lastEvaluatedKey]);

  /**
   * Load initial words
   */
  useEffect(() => {
    fetchWords(false);
  }, []); // Empty dependency array - load once on mount

  /**
   * Update grouped words when words or groupBy changes
   */
  useEffect(() => {
    if (words.length > 0) {
      const grouped = groupWordsByDate(words, groupBy);
      setGroupedWords(grouped);
    } else {
      setGroupedWords({});
    }
  }, [words, groupBy]);

  /**
   * Load more words (for infinite scroll)
   */
  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      fetchWords(true, searchQuery);
    }
  }, [hasMore, isLoading, fetchWords, searchQuery]);

  /**
   * Refresh the word list
   */
  const refresh = useCallback(() => {
    setLastEvaluatedKey(null);
    setHasMore(true);
    setSearchQuery('');
    fetchWords(false);
  }, [fetchWords]);

  /**
   * Search words by prefix with debounce
   */
  const searchWords = useCallback((query) => {
    const trimmedQuery = query.trim();
    setSearchQuery(query);

    // Clear any existing debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // If query is empty, fetch all words immediately
    if (!trimmedQuery) {
      setLastEvaluatedKey(null);
      setHasMore(true);
      setIsSearching(false);
      fetchWords(false);
      return;
    }

    // Set searching state and debounce the API call
    setIsSearching(true);
    searchDebounceRef.current = setTimeout(() => {
      setLastEvaluatedKey(null);
      setHasMore(true);
      fetchWords(false, trimmedQuery);
    }, 300);
  }, [fetchWords]);

  /**
   * Clear search and show all words
   */
  const clearSearch = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    setSearchQuery('');
    setIsSearching(false);
    setLastEvaluatedKey(null);
    setHasMore(true);
    fetchWords(false);
  }, [fetchWords]);

  /**
   * Create a new word
   */
  const createWord = useCallback(async (word, generateExamples = true) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.createWord(word, generateExamples);

      // All responses now have consistent structure: {message, word, action}
      if (response.action === 'level_updated') {
        // Word existed as mastered, now updated to learning
        // Update the existing word in the list
        setWords((prev) =>
          prev.map((w) => (w.wordId === response.word.wordId ? response.word : w))
        );
      } else if (response.action === 'already_exists') {
        // Word already exists - no state change needed
        // The response contains the existing word for reference
      } else if (response.action === 'created') {
        // New word created - add to the beginning of the list
        setWords((prev) => [response.word, ...prev]);
      }

      return response;
    } catch (err) {
      console.error('Error creating word:', err);
      const errorMessage = err.response?.data?.error || 'Failed to create word';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update a word
   */
  const updateWord = useCallback(async (wordId, data) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.updateWord(wordId, data);

      // Update word in the list
      setWords((prev) =>
        prev.map((word) => (word.wordId === wordId ? response : word))
      );

      return response;
    } catch (err) {
      console.error('Error updating word:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update word';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Regenerate a single example in a word
   */
  const regenerateExample = useCallback(async (wordId, exampleIndex) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.regenerateExample(wordId, exampleIndex);

      // Update word in the list
      setWords((prev) =>
        prev.map((word) => (word.wordId === wordId ? response : word))
      );

      return response;
    } catch (err) {
      console.error('Error regenerating example:', err);
      const errorMessage = err.response?.data?.error || 'Failed to regenerate example';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Delete a word
   */
  const deleteWord = useCallback(async (wordId) => {
    try {
      setIsLoading(true);
      setError(null);

      await apiClient.deleteWord(wordId);

      // Remove word from the list
      setWords((prev) => prev.filter((word) => word.wordId !== wordId));
    } catch (err) {
      console.error('Error deleting word:', err);
      const errorMessage = err.response?.data?.error || 'Failed to delete word';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update a word's learning level (optimistic update)
   */
  const updateLearningLevel = useCallback(async (wordId, learningLevel) => {
    // Optimistic update - immediately update local state
    const previousWords = [...words];
    setWords((prev) =>
      prev.map((word) =>
        word.wordId === wordId ? { ...word, learningLevel } : word
      )
    );

    try {
      const response = await apiClient.updateWordLearningLevel(wordId, learningLevel);
      // Update with server response (in case of any differences)
      setWords((prev) =>
        prev.map((word) => (word.wordId === wordId ? response : word))
      );
      return response;
    } catch (err) {
      // Rollback on error
      setWords(previousWords);
      console.error('Error updating learning level:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update learning level';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [words]);

  /**
   * Change date grouping
   */
  const changeGrouping = useCallback((newGroupBy) => {
    setGroupBy(newGroupBy);
  }, []);

  /**
   * Reset all state (used when user logs out/changes)
   */
  const reset = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    setWords([]);
    setGroupedWords({});
    setIsLoading(false);
    setError(null);
    setHasMore(true);
    setLastEvaluatedKey(null);
    setSearchQuery('');
    setIsSearching(false);
  }, []);

  return {
    words,
    groupedWords,
    groupBy,
    isLoading,
    error,
    hasMore,
    loadMore,
    refresh,
    createWord,
    updateWord,
    regenerateExample,
    deleteWord,
    updateLearningLevel,
    changeGrouping,
    reset,
    // Search functionality
    searchQuery,
    isSearching,
    searchWords,
    clearSearch,
  };
};
